import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, Platform, type StyleProp, type ViewStyle } from 'react-native';
import { Image } from 'expo-image';
import { useTheme } from '../../../src/theme/useTheme';
import { getFlagCdnUrl } from '../../../src/worldcup/helpers/flagCdn.helper';
import { WEB_IMAGE_ALT_DECORATIVE } from '../../../src/shared/helpers/imageWebAlt.helper';
import {
  resolveCompetitionCrest,
  competitionMonogram,
  type CompetitionCrest as CompetitionCrestKind,
} from '../../../src/competitions/helpers/competitionCrest.helper';
import {
  CREST_LOGO_SIZE_RATIO,
  CREST_FLAG_CARD_ZOOM,
  CREST_MONOGRAM_SIZE_RATIO,
  COMPETITION_CREST_TEST_IDS,
} from './CompetitionCrest.constants';
import { getCompetitionCrestStyles } from './CompetitionCrest.styles';

export interface CompetitionCrestProps {
  /** Server-resolved crest (`GET /competitions`). Null means "compose one". */
  logoUrl?: string | null;
  /** The name the user sees — brand name when there is one. Drives flag and monogram. */
  name: string;
  shortName?: string | null;
  /** `brand.flagCode` — states the composed flag instead of inferring it from the name. */
  flagCode?: string | null;
  /** Badge diameter. */
  size: number;
  /** Logo image size; defaults to 72% of the badge. Pass it to keep a surface pixel-identical. */
  logoSize?: number;
  /** Monogram tint — the card tints by sport, the hero uses the accent. */
  monogramColor?: string;
  /** Outer look (background, border, glow) from the call site; size always wins over it. */
  style?: StyleProp<ViewStyle>;
  testID?: string;
}

/**
 * A competition's badge, drawn one of three ways (specs/competitions-hub-client Story 15).
 *
 * One component for the hub card, the hub hero and the competition shell header, which
 * each used to carry their own `logoUrl ? Image : fallback` branch. The flag composition
 * is the reason it exists: a competition whose licensed crest we deliberately do not
 * ship still needs an identity, and a European cup gets the European flag itself,
 * filling the badge, rather than three grey letters.
 */
export const CompetitionCrest: React.FC<CompetitionCrestProps> = ({
  logoUrl,
  name,
  shortName,
  flagCode,
  size,
  logoSize,
  monogramColor,
  style,
  testID,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getCompetitionCrestStyles(theme), [theme]);

  const resolved = useMemo(
    () => resolveCompetitionCrest({ logoUrl, name, shortName, flagCode }),
    [logoUrl, name, shortName, flagCode],
  );

  // A flag that 404s must degrade to the monogram, not to an empty circle (AC-15.6).
  // Keyed on the flag itself so a recycled row retrying a different flag starts clean.
  const flagKey = resolved.kind === 'flag' ? resolved.countryCode : null;
  const [flagFailed, setFlagFailed] = useState(false);
  useEffect(() => setFlagFailed(false), [flagKey]);
  const crest: CompetitionCrestKind = resolved.kind === 'flag' && flagFailed
    ? { kind: 'monogram', monogram: competitionMonogram({ name, shortName }) }
    : resolved;

  const flagUri = crest.kind === 'flag' ? getFlagCdnUrl(crest.countryCode) : null;

  const sizeStyle = { width: size, height: size, borderRadius: size / 2 };
  const rootStyle = [styles.root, style, crest.kind === 'logo' && styles.plate, sizeStyle];

  if (crest.kind === 'logo') {
    const px = logoSize ?? Math.round(size * CREST_LOGO_SIZE_RATIO);
    return (
      <View style={rootStyle} testID={testID}>
        <Image
          source={{ uri: crest.logoUrl }}
          style={{ width: px, height: px }}
          contentFit="contain"
          cachePolicy="memory-disk"
          testID={COMPETITION_CREST_TEST_IDS.logo}
          accessibilityLabel={WEB_IMAGE_ALT_DECORATIVE}
          alt={WEB_IMAGE_ALT_DECORATIVE}
        />
      </View>
    );
  }

  if (crest.kind === 'flag' && flagUri != null) {
    // Zoomed and re-centred so the flag card, not the asset's padding, fills the badge.
    const flagPx = Math.round(size * CREST_FLAG_CARD_ZOOM);
    const flagOffset = Math.round((size - flagPx) / 2);
    return (
      <View style={rootStyle} testID={testID}>
        <Image
          source={{ uri: flagUri, cacheKey: flagUri }}
          style={[styles.flagImage, { width: flagPx, height: flagPx, top: flagOffset, left: flagOffset }]}
          contentFit="cover"
          cachePolicy="memory-disk"
          recyclingKey={Platform.OS === 'web' ? undefined : flagUri}
          onError={() => setFlagFailed(true)}
          testID={COMPETITION_CREST_TEST_IDS.flag}
          accessibilityLabel={WEB_IMAGE_ALT_DECORATIVE}
          alt={WEB_IMAGE_ALT_DECORATIVE}
        />
      </View>
    );
  }

  const monogram = crest.kind === 'monogram'
    ? crest.monogram
    : competitionMonogram({ name, shortName });

  return (
    <View style={rootStyle} testID={testID}>
      <Text
        style={[
          styles.monogram,
          { fontSize: Math.round(size * CREST_MONOGRAM_SIZE_RATIO), color: monogramColor ?? theme.accent },
        ]}
        testID={COMPETITION_CREST_TEST_IDS.monogram}
      >
        {monogram}
      </Text>
    </View>
  );
};
